import { apiHosts } from "@/api/constants/apiHosts";
import { ToastOptionsError, ToastOptionsSuccess } from "@modernary/mui/components/toasts";
import { apiService, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import type { AxiosResponse } from "axios";

import type { Person, PersonCreate, PersonQuery } from "./person";

const URL = "people";
const ENTITY_NAME = "Person";
const axiosParams = { baseURL: import.meta.env.VITE_AQMS_API_BASE_URL };
const api = apiService<Person>(apiHosts.AQMS, axiosParams);

async function applyAqmsDetails(person: Person): Promise<Person> {
  const response = await api.get(`${URL}/${person.id}/aqms-details`, {
    successToastCb: (options: ToastOptionsSuccess) => {
      options.hide = true;
    },
    errorToastCb: (options: ToastOptionsError) => {
      options.hideStatusCodes = [404];
    },
  });
  return { ...person, ...(response?.data ?? {}) };
}

async function saveAqmsDetails(person: Person | (PersonCreate & { id: number })) {
  return api.put(`${URL}/${person.id}/aqms-details`, {
    id: person.id,
    hasDustCard: person.hasDustCard,
    dustCardNumber: person.dustCardNumber,
    dustCardExpirationDate: person.dustCardExpirationDate,
  });
}

const getPeople = (params: PersonQuery, config: PagingApiAndNotifierConfig = {}) =>
  api
    .getPaged(URL, {
      params,
      paramsSerializer: serializeQueryParams,
      abort: config.abortMethod,
      successToastCb: (options: ToastOptionsSuccess) => {
        options.hide = true;
        config.successToastCb?.(options);
      },
      errorToastCb: (options: ToastOptionsError) => {
        options.fetched = ENTITY_NAME;
        config.errorToastCb?.(options);
      },
    });

const getPersonById = async (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const response = await api.get(`${URL}/${id}`, {
    successToastCb: (options: ToastOptionsSuccess) => {
      options.hide = true;
      config.successToastCb?.(options);
    },
    errorToastCb: (options: ToastOptionsError) => {
      options.fetched = ENTITY_NAME;
      config.errorToastCb?.(options);
    },
  });
  return { ...response, data: await applyAqmsDetails(response.data) } as AxiosResponse<Person>;
};

const createPerson = async (person: PersonCreate, config: PagingApiAndNotifierConfig = {}) => {
  const response = await api.post(URL, person, {
    successToastCb: (options: ToastOptionsSuccess) => {
      options.created = ENTITY_NAME;
      config.successToastCb?.(options);
    },
    errorToastCb: (options: ToastOptionsError) => {
      options.created = ENTITY_NAME;
      config.errorToastCb?.(options);
    },
  });
  await saveAqmsDetails(response.data);
  return response;
};

const updatePerson = async (person: Person & PersonCreate, config: PagingApiAndNotifierConfig = {}) => {
  const response = await api.put(`${URL}/${person.id}`, person, {
    successToastCb: (options: ToastOptionsSuccess) => {
      options.updated = ENTITY_NAME;
      config.successToastCb?.(options);
    },
    errorToastCb: (options: ToastOptionsError) => {
      options.updated = ENTITY_NAME;
      config.errorToastCb?.(options);
    },
  });
  await saveAqmsDetails(person);
  return response;
};

const deletePersonById = (id: string | number, config: PagingApiAndNotifierConfig = {}) =>
  api.delete(`${URL}/${id}`, {
    successToastCb: (options: ToastOptionsSuccess) => {
      options.deleted = ENTITY_NAME;
      config.successToastCb?.(options);
    },
    errorToastCb: (options: ToastOptionsError) => {
      options.deleted = ENTITY_NAME;
      config.errorToastCb?.(options);
    },
  });

export { createPerson, deletePersonById, getPeople, getPersonById, updatePerson };
